import { Button, Flex, Td, Tr, useToast } from "@chakra-ui/react";
import { ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit, FaTrash } from "react-icons/fa";
import {
  OrganizationRoleType,
  useDeleteOrgUserMutation,
  useUpdateOrgUserRoleMutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { MultiSelector } from "../../../../common/input/Selector/MultiSelector";
import { SelectorValue } from "../../../../common/input/Selector/SelectorValue";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { OrgDto, updateSelectedOrg } from "../../../../common/redux/reducers/authStateReducer";
import { ConfirmationModalConfig } from "../../../../common/useConfirmationModal";
import { User } from "./types";
import { useRoleName } from "./useRoleName";
import { useSelectableRoles } from "./useSelectableRoles";

interface Props {
  user: User;
  confirmationModal: {
    show: (config: ConfirmationModalConfig) => void;
    modal: ReactElement;
  };
}

export const UserRow = ({ user, confirmationModal }: Props) => {
  const authState = useLoggedInWithOrgContextState();
  const [deleteUser] = useDeleteOrgUserMutation();
  const [updateRole] = useUpdateOrgUserRoleMutation();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { t } = useTranslation();
  const displayer = useApiError();
  const [showChangeRole, setShowChangeRole] = useState(false);
  const selectableRoles = useSelectableRoles();
  const getRoleName = useRoleName();

  const [selectedRoles, setSelectedRoles] = useState<SelectorValue[]>(
    user.roles.map((e) => ({
      value: e,
      label: getRoleName(e),
    }))
  );
  const [isChangingRole, setIsChangingRole] = useState(false);

  const triggerDelete = async () => {
    const result = await deleteUser({
      orgId: authState.selectedOrg.id,
      userId: user.id,
    });
    if ("data" in result) {
      toast({ title: t("Invite deleted!"), status: "success" });
      dispatch(updateSelectedOrg(result.data as OrgDto));
    } else {
      displayer.trigger(result.error);
    }
  };

  const triggerChangeRole = async () => {
    setIsChangingRole(true);
    const result = await updateRole({
      orgId: authState.selectedOrg.id,
      userId: user.id,
      updateOrgUserRoleRequest: {
        newRoles: selectedRoles.map((e) => e.value as OrganizationRoleType),
      },
    });
    if ("data" in result) {
      toast({ title: t("Role changed!"), status: "success" });
      dispatch(updateSelectedOrg(result.data as OrgDto));
      setShowChangeRole(false);
    } else {
      displayer.trigger(result.error);
    }
    setIsChangingRole(false);
  };

  return (
    <>
      <Tr key={user.id}>
        <Td>{user.name}</Td>
        <Td>{user.email}</Td>
        <Td>
          {showChangeRole ? (
            <MultiSelector
              value={selectedRoles}
              options={selectableRoles}
              noOptionsAvailableMessage={t("No roles available")}
              onChange={(value) => {
                const roleTypes = value.map((e) => e.value as OrganizationRoleType);
                let filteredRoles = value;

                if (roleTypes.includes(OrganizationRoleType.Admin)) {
                  filteredRoles = filteredRoles.filter((e) => e.value === OrganizationRoleType.Admin);
                }

                if (
                  roleTypes.includes(OrganizationRoleType.ContractCreator) &&
                  roleTypes.includes(OrganizationRoleType.ContractViewer)
                ) {
                  filteredRoles = filteredRoles.filter((e) => e.value !== OrganizationRoleType.ContractViewer);
                }

                if (
                  roleTypes.includes(OrganizationRoleType.SourcingCreator) &&
                  roleTypes.includes(OrganizationRoleType.SourcingViewer)
                ) {
                  filteredRoles = filteredRoles.filter((e) => e.value !== OrganizationRoleType.SourcingViewer);
                }

                if (
                  roleTypes.includes(OrganizationRoleType.ProjectCreator) &&
                  roleTypes.includes(OrganizationRoleType.ProjectViewer)
                ) {
                  filteredRoles = filteredRoles.filter((r) => r.value !== OrganizationRoleType.ProjectViewer);
                }
                setSelectedRoles(filteredRoles);
              }}
            />
          ) : (
            user.roles.map((r) => getRoleName(r)).join(", ")
          )}
        </Td>
        <Td>
          {showChangeRole && (
            <>
              <Button
                mr="5px"
                variant={"solid"}
                colorScheme="blue"
                size={"sm"}
                isLoading={isChangingRole}
                onClick={triggerChangeRole}
              >
                {t("Save")}
              </Button>
              <Button variant={"solid"} colorScheme="orange" size={"sm"} onClick={() => setShowChangeRole(false)}>
                {t("Cancel")}
              </Button>
            </>
          )}
          {user.id !== authState.me.id && !user.roles.includes(OrganizationRoleType.Owner) && !showChangeRole && (
            <Flex columnGap="1" rowGap="1">
              <Button
                variant={"outline"}
                colorScheme="blue"
                size={"sm"}
                leftIcon={<FaEdit />}
                onClick={() => setShowChangeRole(true)}
              >
                {t("Change role")}
              </Button>
              <Button
                variant={"outline"}
                colorScheme="red"
                size={"sm"}
                leftIcon={<FaTrash />}
                onClick={() => {
                  confirmationModal.show({
                    title: t("Remove person?"),
                    description: t("Are you sure you want to remove this person?"),
                    onConfirmed: async () => {
                      await triggerDelete();
                    },
                  });
                }}
              >
                {t("Remove")}
              </Button>
            </Flex>
          )}
        </Td>
      </Tr>
    </>
  );
};
